import React, { useState, useContext } from "react";
import axios from "axios";
import { Modal, Form, Spinner } from "react-bootstrap";
import { TranslatorContext } from "../../context/Translator";
import { ButtonComponent, AnchorComponent } from "../../components/elements";
import IconFieldComponent from "../../components/fields/IconFieldComponent";
import LogoComponent from "../../components/LogoComponent";
import "./LoginPage.css";
import { API_URL, APP_PREFIX_PATH, getApiMessage } from "../../constant/constant";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

/**
 * Public page — same layout as login (mc-auth). Browser route: /delete-user
 */
export default function DeleteUserPage() {
    const { t } = useContext(TranslatorContext);

    const [deleteEmail, setDeleteEmail] = useState("");
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteReason, setDeleteReason] = useState("");
    const [deleteEmailError, setDeleteEmailError] = useState("");
    const [deletePasswordError, setDeletePasswordError] = useState("");
    const [deleteGeneralError, setDeleteGeneralError] = useState("");
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [deleteSuccessModal, setDeleteSuccessModal] = useState(false);

    const resetDeleteErrors = () => {
        setDeleteEmailError("");
        setDeletePasswordError("");
        setDeleteGeneralError("");
    };

    const handleDeleteAccount = async (e) => {
        e.preventDefault();
        resetDeleteErrors();
        let hasError = false;

        if (!deleteEmail.trim()) {
            setDeleteEmailError(t("enter_your_email"));
            hasError = true;
        }
        if (!deletePassword) {
            setDeletePasswordError(t("enter_your_password"));
            hasError = true;
        }
        if (hasError) return;

        const body = {
            email: deleteEmail.trim(),
            password: deletePassword,
        };
        if (deleteReason.trim()) {
            body.delete_reason = deleteReason.trim();
        }

        setDeleteSubmitting(true);
        try {
            const res = await axios.post(API_URL + "/delete_my_account", body);
            const d = res.data;
            if (d && (d.success === false || d.success === 0)) {
                setDeleteGeneralError(getApiMessage(d) || d.message || t("delete_account_failed"));
                return;
            }
            setDeleteSuccessModal(true);
            setDeleteEmail("");
            setDeletePassword("");
            setDeleteReason("");
        } catch (error) {
            const msg =
                error.response?.data?.message ||
                getApiMessage(error.response?.data) ||
                error.response?.data?.msg ||
                error.response?.data?.error ||
                t("delete_account_failed");
            setDeleteGeneralError(typeof msg === "string" ? msg : t("delete_account_failed"));
            console.error(error);
        } finally {
            setDeleteSubmitting(false);
        }
    };

    return (
        <div className="mc-auth">
            <img src="/2024/clinic/admin/images/logo.png" alt="pattern" className="mc-auth-pattern" />
            <div className="mc-auth-group">
                <form className="mc-auth-form" onSubmit={handleDeleteAccount}>
                    <LogoComponent
                        src="/2024/clinic/admin/images/logo.png"
                        alt="logo"
                        href="/"
                        className="mc-auth-logo"
                        style={{ width: "55px" }}
                    />

                    <h5 className="text-center mb-4 fw-semibold">{t("delete_my_account")}</h5>

                    <IconFieldComponent
                        icon="email"
                        type="email"
                        classes="w-100 h-sm"
                        placeholder={t("enter_your_email")}
                        value={deleteEmail}
                        onChange={(e) => {
                            setDeleteEmail(e.target.value);
                            setDeleteEmailError("");
                        }}
                        maxLength={100}
                    />
                    <div className="d-flex justify-content-left">
                        <p
                            className="text-danger form-text"
                            style={{ marginLeft: "5px", marginTop: "-20px" }}
                        >
                            {deleteEmailError}
                        </p>
                    </div>

                    <IconFieldComponent
                        icon="lock"
                        type="password"
                        classes="w-100 h-sm"
                        placeholder={t("enter_your_password")}
                        passwordVisible={true}
                        value={deletePassword}
                        onChange={(e) => {
                            setDeletePassword(e.target.value);
                            setDeletePasswordError("");
                        }}
                        maxLength={128}
                    />
                    <div className="d-flex justify-content-left">
                        <p className="text-danger form-text" style={{ marginLeft: "5px", marginTop: "-20px" }}>
                            {deletePasswordError}
                        </p>
                    </div>

                    <Form.Group className="mb-3 text-start">
                        <Form.Label className="small text-muted mb-1">{t("delete_reason_optional")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            className="rounded"
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            maxLength={500}
                        />
                    </Form.Group>

                    {deleteGeneralError ? (
                        <p className="text-danger small text-center mb-2">{deleteGeneralError}</p>
                    ) : null}

                    <ButtonComponent className="mc-auth-btn h-sm" type="submit" disabled={deleteSubmitting}>
                        {deleteSubmitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {t("delete_my_account")}
                            </>
                        ) : (
                            t("delete_my_account")
                        )}
                    </ButtonComponent>

                    <AnchorComponent className="mc-auth-forgot d-block text-center mt-2" to={`${APP_PREFIX_PATH}/`}>
                        {t("sign_in")}
                    </AnchorComponent>
                </form>
            </div>

            <Modal show={deleteSuccessModal} onHide={() => setDeleteSuccessModal(false)} className="custom-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="w-100 text-center">{t("success")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <p className="lead mb-0">{t("account_deleted_success")}</p>
                </Modal.Body>
                <Modal.Footer className="border-0 justify-content-center">
                    <button
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={() => setDeleteSuccessModal(false)}
                    >
                        OK
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
